import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
} from '@mui/material';
import Layout from '../components/Layout';
import { serverService } from '../services/serverService';
import { tenantService } from '../services/tenantService';
import type { Tenant } from '../types/tenant';

const ServerForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    tenantId: '',
    name: '',
    description: '',
    isActive: true,
  });
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEdit);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTenants();
    if (isEdit && id) {
      loadServer(id);
    }
  }, [isEdit, id]);

  const loadTenants = async () => {
    try {
      const data = await tenantService.getAll();
      setTenants(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Şirket grupları yüklenirken bir hata oluştu');
    }
  };

  const loadServer = async (serverId: string) => {
    try {
      setLoadingData(true);
      setError(null);
      const server = await serverService.getById(serverId);
      setFormData({
        tenantId: server.tenantId,
        name: server.name,
        description: server.description || '',
        isActive: server.isActive ?? true,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Sunucu yüklenirken bir hata oluştu');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEdit && id) {
        await serverService.update({
          id,
          name: formData.name,
          description: formData.description,
          isActive: formData.isActive,
        });
      } else {
        await serverService.create({
          tenantId: formData.tenantId,
          name: formData.name,
          description: formData.description,
        });
      }
      navigate('/servers');
    } catch (err: any) {
      setError(err.response?.data?.message || 'İşlem sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/servers');
  };

  if (loadingData) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Typography variant="h4" component="h1" gutterBottom>
        {isEdit ? 'Sunucu Düzenle' : 'Yeni Sunucu Oluştur'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 2 }}>
        <Box component="form" onSubmit={handleSubmit}>
          {!isEdit && (
            <FormControl fullWidth margin="normal" required>
<InputLabel>Şirket Grubu</InputLabel>
                <Select
                  value={formData.tenantId}
                  onChange={(e) => setFormData({ ...formData, tenantId: e.target.value })}
                  label="Şirket Grubu"
                >
                  <MenuItem value="">Şirket grubu seçiniz</MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            label="Sunucu Adı"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            placeholder="Sunucu adını giriniz"
          />

          <TextField
            fullWidth
            label="Açıklama"
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            placeholder="Sunucu açıklamasını giriniz"
          />

          {isEdit && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  color="primary"
                  aria-label="Sunucu aktif / pasif"
                />
              }
              label="Aktif"
              sx={{ mt: 2, display: 'block' }}
            />
          )}

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
            >
              İptal
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : isEdit ? 'Güncelle' : 'Oluştur'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Layout>
  );
};

export default ServerForm;












